/**
 * Armada Dimensions Section
 *
 * Responsibilities:
 *  1. Accordion open/close state — keeps `is-open` class in sync with <details> toggle
 *     so the caret-rotation CSS transition fires correctly alongside CollapsibleDetails.
 *  2. Mobile table scroll indicator — tracks horizontal scroll of the table and
 *     moves a custom 2px thumb to reflect scroll progress (replaces the native scrollbar).
 *  3. Active variant column — highlights the selected size via `variantChange` pub/sub.
 *     On mobile the table scroller also brings that column into view: on load, on
 *     resize, and when the size changes on the PDP picker. Only the section's own
 *     horizontal scroller is ever moved — never the document — because nested
 *     overflow scroll on iOS can drag the page with it (AAS-5383). A change that
 *     came from a tap on the table itself does not scroll: the shopper's finger is
 *     already on that column, and that was the path that jumped.
 *  4. Two-way size select (AAS-5383) — clicking a value column selects that variant
 *     on the PDP picker. The section is not in the tab order (tap-focus scrolled
 *     the page on iOS). Keyboard users change size with the PDP picker.
 */

(() => {
  'use strict';

  /* ============================================================
     1. Accordion: keep is-open class in sync with <details>
     ============================================================ */
  const initAccordions = (root) => {
    root.querySelectorAll('.armada-dimensions__accordion-item details').forEach((details) => {
      details.addEventListener('toggle', () => {
        details.classList.toggle('is-open', details.open);
      });
    });
  };

  /* ============================================================
     2. Mobile scroll indicator
     ============================================================ */
  const initScrollIndicator = (scrollEl, indicatorEl) => {
    const thumb = indicatorEl.querySelector('.armada-dimensions__scroll-thumb');
    if (!thumb) return;

    const track = indicatorEl.querySelector('.armada-dimensions__scroll-track');
    if (!track) return;

    const section = scrollEl.closest('.armada-dimensions');
    const thumbMinRaw = section
      ? parseFloat(getComputedStyle(section).getPropertyValue('--dim-scroll-thumb-min'))
      : NaN;
    const thumbMin = Number.isFinite(thumbMinRaw) ? thumbMinRaw * 10 : 24;

    const viewport = scrollEl.closest('.armada-dimensions__table-viewport');
    const labelsCol = section ? section.querySelector('.armada-dimensions__col--labels') : null;

    const EDGE = 4;
    const syncFades = (scrollLeft, maxScroll) => {
      if (!viewport) return;
      if (labelsCol) {
        viewport.style.setProperty('--dim-labels-width', `${labelsCol.getBoundingClientRect().width}px`);
      }
      const canScroll = maxScroll > EDGE;
      viewport.classList.toggle('has-scroll-left', canScroll && scrollLeft > EDGE);
      viewport.classList.toggle('has-scroll-right', canScroll && scrollLeft < maxScroll - EDGE);
    };

    const update = () => {
      const scrollLeft = scrollEl.scrollLeft;
      const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
      syncFades(scrollLeft, maxScroll);

      if (maxScroll <= EDGE) {
        indicatorEl.hidden = true;
        return;
      }
      indicatorEl.hidden = false;

      const trackWidth = track.offsetWidth;
      const ratio = scrollEl.clientWidth / scrollEl.scrollWidth;
      const thumbWidth = Math.max(trackWidth * ratio, thumbMin);

      const scrollRatio = scrollLeft / maxScroll;
      const maxThumbLeft = trackWidth - thumbWidth;
      const thumbLeft = scrollRatio * maxThumbLeft;

      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${thumbLeft}px)`;
    };

    scrollEl.addEventListener('scroll', update, { passive: true });

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(update)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(scrollEl);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    update();
  };

  const initScrollIndicators = (root) => {
    root.querySelectorAll('.armada-dimensions__scroll-indicator[data-scroll-for]').forEach((indicator) => {
      const targetId = indicator.getAttribute('data-scroll-for');
      const scrollEl = document.getElementById(targetId);
      if (scrollEl) {
        initScrollIndicator(scrollEl, indicator);
      }
    });
  };

  /* ============================================================
     3. Highlight the column of the currently-selected variant
     ============================================================ */
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const REDUCED_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isOnScreen = (el) => {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < viewportHeight;
  };

  const CANCEL_EVENTS = ['touchstart', 'touchmove', 'wheel', 'keydown'];

  /**
   * Pin the document where it is for `duration` ms.
   *
   * Moving a nested horizontal scroller is supposed to leave the page alone, but
   * iOS Safari sometimes drags the document along (the reason scroll-on-change was
   * pulled in AAS-5383). Cheap insurance: sample each frame while the scroll
   * settles and put the page back if it moved. Any real input gesture cancels the
   * guard immediately — the shopper always wins over it.
   */
  const keepPageStill = (duration) => {
    const pageX = window.scrollX;
    const pageY = window.scrollY;
    const deadline = performance.now() + duration;
    let cancelled = false;

    const cancel = () => {
      cancelled = true;
    };
    const stopListening = () => {
      CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel));
    };
    CANCEL_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true, once: true }));

    const tick = () => {
      if (cancelled) {
        stopListening();
        return;
      }
      if (window.scrollY !== pageY || window.scrollX !== pageX) {
        window.scrollTo(pageX, pageY);
      }
      if (performance.now() < deadline) {
        requestAnimationFrame(tick);
        return;
      }
      stopListening();
    };

    requestAnimationFrame(tick);
  };

  const scrollActiveColumnIntoView = (section, { smooth = false } = {}) => {
    if (!MOBILE_MQ.matches) return;

    const scrollEl = section.querySelector('.armada-dimensions__table-scroll');
    const activeCol = section.querySelector('.armada-dimensions__col--active:not(.armada-dimensions__col--labels)');
    if (!scrollEl || !activeCol) return;

    const labelsCol = section.querySelector('.armada-dimensions__col--labels');
    const scrollRect = scrollEl.getBoundingClientRect();
    const labelsWidth = labelsCol
      ? labelsCol.getBoundingClientRect().width
      : 0;
    const activeRect = activeCol.getBoundingClientRect();
    const colLeft = activeRect.left - scrollRect.left + scrollEl.scrollLeft;
    const colRight = colLeft + activeRect.width;
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    let targetScroll = scrollEl.scrollLeft;

    const visibleLeft = scrollEl.scrollLeft + labelsWidth;
    const visibleRight = scrollEl.scrollLeft + scrollEl.clientWidth;

    if (colLeft < visibleLeft) {
      targetScroll = colLeft - labelsWidth;
    } else if (colRight > visibleRight) {
      targetScroll = colRight - scrollEl.clientWidth;
    }

    const maxScrollBeforeOverlap = Math.max(0, colLeft - labelsWidth);
    targetScroll = Math.min(targetScroll, maxScrollBeforeOverlap);
    targetScroll = Math.max(0, Math.min(maxScroll, targetScroll));

    if (targetScroll === scrollEl.scrollLeft) return;

    // Smooth only when the table is actually on screen. A size change on the
    // picker happens with the table far below the fold, so animating it is motion
    // nobody sees — jump straight to the column instead.
    const animate = smooth && !REDUCED_MOTION_MQ.matches && isOnScreen(scrollEl);

    if (animate && typeof scrollEl.scrollTo === 'function') {
      scrollEl.scrollTo({ left: targetScroll, behavior: 'smooth' });
      keepPageStill(500);
      return;
    }

    scrollEl.scrollLeft = targetScroll;
    keepPageStill(50);
  };

  const setActiveColumn = (section, variantId) => {
    section.querySelectorAll('.armada-dimensions__col[data-variant-id]').forEach((col) => {
      const match = String(col.dataset.variantId) === String(variantId);
      col.classList.toggle('armada-dimensions__col--active', match);
      if (match) col.setAttribute('aria-current', 'true');
      else col.removeAttribute('aria-current');
      const firstCell = col.querySelector('.armada-dimensions__cell--value');
      if (firstCell) firstCell.classList.toggle('armada-dimensions__cell--active-size', match);
    });
  };

  const initActiveColumnScroll = (section) => {
    if (section.dataset.activeColumnScrollBound === 'true') return;
    section.dataset.activeColumnScrollBound = 'true';

    const scrollEl = section.querySelector('.armada-dimensions__table-scroll');
    if (!scrollEl) return;

    const onLayoutChange = () => {
      scrollActiveColumnIntoView(section);
    };

    window.addEventListener('resize', onLayoutChange, { passive: true });
    MOBILE_MQ.addEventListener('change', onLayoutChange);

    requestAnimationFrame(() => {
      scrollActiveColumnIntoView(section);
    });
  };

  /**
   * Set by a tap on a value column, cleared by the `variantChange` it triggers.
   *
   * Keyed on BOTH the variant and a deadline, because a tap does not always
   * produce a variant change to clear it — `selectVariantById` returns true
   * without dispatching when that variant is already current. A stale flag can
   * then only ever mute a change to that same variant, and only briefly; every
   * other size change still scrolls. The window is generous because the picker
   * round-trips through a section render before publishing.
   */
  let suppressScrollFor = { variantId: null, until: 0 };

  const consumeScrollSuppression = (variantId) => {
    const { variantId: suppressedId, until } = suppressScrollFor;
    const matches = suppressedId != null
      && String(suppressedId) === String(variantId)
      && performance.now() < until;
    suppressScrollFor = { variantId: null, until: 0 };
    return matches;
  };

  const initVariantHighlight = (section) => {
    if (section.dataset.variantHighlightBound === 'true') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    section.dataset.variantHighlightBound = 'true';

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (variant?.id == null) return;
      setActiveColumn(section, variant.id);

      if (consumeScrollSuppression(variant.id)) return;

      scrollActiveColumnIntoView(section, { smooth: true });
    });
  };

  /* ============================================================
     4. Click a size column → select that variant on the PDP picker
     ============================================================ */
  const initColumnSelect = (section) => {
    if (section.dataset.columnSelectBound === 'true') return;
    const table = section.querySelector('.armada-dimensions__table');
    if (!table) return;
    section.dataset.columnSelectBound = 'true';

    table.addEventListener('click', (event) => {
      const col = event.target.closest?.('.armada-dimensions__col[data-variant-id]');
      if (!col || !table.contains(col)) return;
      if (col.classList.contains('armada-dimensions__col--active')) return;

      const { variantId } = col.dataset;
      if (!variantId) return;

      const picker = document.querySelector('[id^="MainProduct-"] variant-selects');
      if (typeof picker?.selectVariantById !== 'function') return;

      // The resulting variantChange must not scroll the table: the shopper is
      // touching this column already, and scrolling under a finger is what took
      // the page with it on iOS. Set before the call — the picker can dispatch
      // synchronously — and lifted again if no change went out.
      suppressScrollFor = { variantId, until: performance.now() + 3000 };
      if (picker.selectVariantById(variantId) === false) {
        suppressScrollFor = { variantId: null, until: 0 };
      }
    });
  };

  /* ============================================================
     Init
     ============================================================ */
  const initSection = (section) => {
    initAccordions(section);
    initScrollIndicators(section);
    initVariantHighlight(section);
    initActiveColumnScroll(section);
    initColumnSelect(section);
  };

  const init = (root = document) => {
    root.querySelectorAll('.armada-dimensions').forEach((section) => {
      initSection(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll('.armada-dimensions').forEach((section) => {
      initSection(section);
    });
  });
})();
